import React, {
  FunctionComponent,
  ReactNode,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ExaminedResultEnum,
  LeftRightEnum,
  PHIPhysical,
  Semester,
  StrabismusKindEnum,
  Student,
  StudentPHIAll,
  TeeInspection,
  YearSemClasses,
} from '../../model';
import {
  CheckBoxField,
  DateTimeField,
  DropdownCheckListField,
  ExaminationReasonField,
  InputDropdownGroupField,
  MyInputGroup,
  TeeSheetWithButton,
  TextField,
} from '../../components';
import {
  Button,
  Col,
  Dropdown,
  DropdownButton,
  Modal,
  Row,
  Table,
} from 'react-bootstrap';
import { Form } from 'react-final-form';
import { DateTime } from 'luxon';
import apis, { ApiError, E_GRADE_INVALID } from '../../apis';
import { I18N } from '../../i18n-raw';
import {
  ExaminedResultToTeeEnum,
  ExaminedResultToTeeFollowUpEnum,
  TeeFollowUpExaminationEnum,
  TeeInitialExaminationEnum,
  TeeToExaminedEnum,
} from '../../utils/tee';
import { HoverToolTip } from '../../components/HoverTooltip';
import { PhiysicalTeeCalculators } from '../TableHeaders/TeeHeader';
import { toast } from 'react-toastify';

type Props = {
  physicals: PHIPhysical[];
  editable: boolean;
  student: StudentPHIAll;
  schoolId: string;
  semesters: Semester[];
  yearSems: YearSemClasses[];
  grade: number;
  onApiError: (e: any) => void;
  onValueUpdated: () => void;
};

export const PhysicalSection: FunctionComponent<Props> = ({
  editable,
  physicals,
  student,
  schoolId,
  semesters,
  yearSems,
  grade,
  onApiError,
  onValueUpdated,
}) => {
  const [inputing, setInputing] = useState(false);
  const [inputManage, setInputManage] = useState(false);

  const pageData = useMemo(() => {
    const p = physicals
      .sort((a, b) => b.year - a.year)
      .find((s) => s.grade === grade && s.sem === 1);
    if (p) return PHIPhysicalToPageData(p);
    return undefined;
  }, [physicals, grade]);

  function onValueUpdate(value: PageData, examined: boolean) {
    const editableGrades = [1, 4, 7, 10];
    if (!editableGrades.includes(grade)) {
      onApiError(new ApiError(E_GRADE_INVALID));
      setInputing(false);
      return;
    }



    const currentSemester = semesters.find((s) => s.isNow);
    if (!currentSemester) {
      console.error('找不到目前學期');
      onApiError(new ApiError(E_GRADE_INVALID));
      setInputing(false);
      return;
    }

    // 找「目前學年度 sem=1 且目前年級」的資料
    // 重讀時，例如 113-1 七年級、114-1 七年級，會各自對應到不同 year
    const currentYearGrade = yearSems.find(
      (s) => s.year === currentSemester.year && s.sem === 1 && s.grade === grade
    );

    if (!currentYearGrade) {
      console.error('找不到目前年級資料');
      onApiError(new ApiError(E_GRADE_INVALID));
      setInputing(false);
      return;
    }

    const physical = PageDataToPHIPhysical({
      ...generatePhysical(grade, student.sex),
      ...value,
    });

    // const promise = examined
    //   ? apis.InsertStudentPhysical(student.pid, {
    //     ...physical,
    //     grade: currentYearGrade.grade,
    //     sem: 1,
    //     year: currentYearGrade.year,
    //     yearClassId:
    //       physical.yearClassId || currentYearGrade.yearClassId || 0,
    //   })
    //   : apis.deleteStudentPhysical(student.pid, currentYearGrade.year, 1);

    const promise = apis.InsertStudentPhysical(
      student.pid,
      {
        ...physical,
        grade: currentYearGrade.grade,
        sem: 1,
        year: currentYearGrade.year,
        yearClassId:
          physical.yearClassId ||
          currentYearGrade.yearClassId ||
          0,
      }
    );

    promise
      .catch(onApiError)
      .then(() => {
        onValueUpdated();
      })
      .finally(() => {
        setInputing(false);
      });
  }

  return (
    <>
      <div className="sheet-title">
        身體診察 {grade ?? ''}年級
        <Button
          key="cell-edit"
          className="border-0 px-2 py-1"
          variant="outline-primary"
          size="sm"
          onClick={() => {
            setInputing(true);
          }}
          disabled={pageData && pageData?.schoolId !== schoolId}
        >
          {pageData && pageData.schoolId !== schoolId ? (
            <HoverToolTip tooltip={'無法修改非本校資料'}>
              <span className="feather icon-x text-danger border border-danger rounded" />
            </HoverToolTip>
          ) : (
            <span className="feather icon-edit" />
          )}
        </Button>
      </div>
      <div className="sheet-title">
        健康管理
        <Button
          key="cell-edit"
          className="border-0 px-2 py-1"
          variant="outline-primary"
          size="sm"
          onClick={() => {
            setInputManage(true);
          }}
          disabled={pageData && pageData?.schoolId !== schoolId}
        >
          {pageData && pageData.schoolId !== schoolId ? (
            <HoverToolTip tooltip={'無法修改非本校資料'}>
              <span className="feather icon-x text-danger border border-danger rounded" />
            </HoverToolTip>
          ) : (
            <span className="feather icon-edit" />
          )}
        </Button>
      </div>
      <Table bordered className="student-phi">
        <colgroup>
          <col width={'20%'} />
          <col width={'80%'} />
        </colgroup>
        <tbody>
          <tr>
            <td>學期/日期</td>
            <td>
              {pageData
                ? pageData.examDate?.toFormat('yyyy/MM/dd') ?? '無檢查日期'
                : '尚未受檢'}
            </td>
          </tr>
          {pageData ? generatePhysicalSummarize(pageData) : <></>}
        </tbody>
      </Table>
      {inputing ? (
        <PhysicalModel
          inputing={inputing}
          physical={pageData}
          grade={grade}
          student={student}
          onClose={() => setInputing(false)}
          onValueUpdate={onValueUpdate}
        />
      ) : (
        <></>
      )}
      {inputManage ? (
        <PhysicalManageModel
          inputing={inputManage}
          physical={pageData}
          grade={grade}
          student={student}
          onClose={() => setInputManage(false)}
          onValueUpdate={(v) => onValueUpdate(v, true)}
        />
      ) : (
        <></>
      )}
    </>
  );
};

type ModelProps = {
  inputing: boolean;
  physical?: PageData;
  student: StudentPHIAll;
  grade: number;
  onClose: () => void;
  onValueUpdate: (v: PageData, examined: boolean) => void;
};

function PhysicalModel({
  inputing,
  physical,
  student,
  grade,
  onClose,
  onValueUpdate,
}: ModelProps): JSX.Element {
  const [examined, setExamined] = useState(
    isPhysicalExamined(physical)
  );

  useEffect(() => {
    setExamined(
      isPhysicalExamined(physical)
    );
  }, [physical]);

  // function isPhysicalExamined(
  //   physical?: PageData
  // ): boolean {
  //   if (!physical) {
  //     return true;
  //   }

  //   const noExaminedValues = [
  //     physical.bpResult,
  //     physical.pulseResult,
  //     physical.colorBlind,
  //     physical.hearing,
  //     physical.torticollis,
  //     physical.cardiopulmonary,
  //     physical.abdomen,
  //     physical.scoliosis,
  //     physical.epidermophytosis,
  //   ];

  //   return !noExaminedValues.every(
  //     (value) =>
  //       Number(value) ===
  //       Number(ExaminedResultEnum.NoExamined)
  //   );
  // }
  function isPhysicalExamined(
    physical?: PageData
  ): boolean {
    if (physical && physical.examDate) {
      return true;
    }
    
    return false;
  }
  

  function generate99state(
    property: string,
    subProperty: string,
    disabled: boolean | ((v: PageData) => boolean),
    content: Record<number, string> = I18N.ExaminationResult
  ): ReactNode {
    const value = physical ? physical[property as keyof PageData] : undefined;
    const labelClass =
      value === ExaminedResultEnum.InitialAbnormal ||
        value === ExaminedResultEnum.FollowUpAbnormal ||
        value === ExaminedResultEnum.FollowUpNormal
        ? 'text-danger'
        : '';
    return (
      <div className="d-flex align-items-center px-2 my-1">
        <InputDropdownGroupField
          className={`rwid-250 mx-1`}
          property={property}
          content={content}
          type="number"
          needGroup={false}
          disabled={disabled}
        />
        <div className={labelClass}>其他</div>
        <TextField
          className="rwid-1250 mx-1"
          property={subProperty}
          disabled={disabled}
        />
      </div>
    );
  }

  function generateExaminedOnly(
    display: string,
    property: string,
    disabled: boolean | ((v: PageData) => boolean),
    content: Record<number, string> = I18N.ExaminationResult
  ): ReactNode {
    const value = physical ? physical[property as keyof PageData] : undefined;
    const labelClass =
      value === ExaminedResultEnum.InitialAbnormal ||
        value === ExaminedResultEnum.FollowUpAbnormal ||
        value === ExaminedResultEnum.FollowUpNormal
        ? 'text-danger'
        : '';
    return (
      <div className="d-flex align-items-center px-2 my-1">
        <InputDropdownGroupField
          className={`rwid-250 ml-1`}
          property={property}
          content={content}
          disabled={disabled}
          type="number"
          maxLength={1}
          needGroup={false}
        />
        <span className={`mx-1 ${labelClass}`}>{display}</span>
      </div>
    );
  }

  return (
    <Modal
      show={inputing}
      aria-labelledby="contained-modal-title-vcenter"
      backdrop="static"
      dialogClassName="modal-dialog-full"
      centered
    >
      <Modal.Header closeButton onHide={onClose}>
        <Modal.Title id="contained-modal-title-vcenter">
          身體診察 -{' '}
          <strong>
            {student.seat} {student.name}
          </strong>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-auto">
        <Form
          initialValues={
            physical ??
            PHIPhysicalToPageData(NoExaminedPhysical)
          }
          decorators={PhiysicalTeeCalculators}
          onSubmit={(v: PageData) => {
            const oralNoExamined =
              examined &&
              Number(v.t01) ===
              Number(TeeInitialExaminationEnum.NoExamined);

            // 身體診察未受檢
            if (!examined && !v.examination) {
              toast.error('請選擇身體診察未受檢原因');
              return;
            }

            // 牙齒未受檢
            if (oralNoExamined && !v.teeExamination) {
              toast.error('請選擇口腔初檢未受檢原因');
              return;
            }

            onValueUpdate(
              {
                ...v,
                // 身體診察
                examination: !examined
                  ? v.examination
                  : null,
                examinationState: !examined
                  ? v.examinationState
                  : null,

                // 牙齒
                teeExamination: oralNoExamined
                  ? v.teeExamination
                  : null,
                teeExaminationState: oralNoExamined
                  ? v.teeExaminationState
                  : null,
              },
              examined
            );
          }}
          validateOnBlur
          subscription={{
            values: true,
          }}
          render={({ handleSubmit, form, values }) => {
            return (
              <Table striped bordered hover className="text-break">
                <colgroup>
                  <col width={'7%'} />
                  <col width={'90%'} />
                </colgroup>
                <thead>
                  <tr>
                    <td className="align-middle">項目</td>
                    <td className="align-middle">
                      <Row noGutters className="align-items-start">
                        <Col xs={12} md={2}>
                          身分證： {student.pid}
                        </Col>

                        <Col xs={12} md={2}>
                          檢查年級： {grade}
                        </Col>

                        <Col xs={12} md={2}>
                          是否受檢：{' '}

                          <DropdownButton
                            className="d-inline mr-2"
                            title={examined ? '是' : '否'}
                            onSelect={(k: string | null) => {
                              setExamined((lastExamine) => {
                                const nextExamine =
                                  k === '0' ? false : true;

                                if (!lastExamine && nextExamine) {
                                  form.reset(
                                    PHIPhysicalToPageData(
                                      generatePhysical(
                                        grade,
                                        student.sex
                                      )
                                    )
                                  );
                                } else if (!nextExamine) {
                                  /*
                                   * 切成未受檢時，保留原本已選過的
                                   * Examination / ExaminationState。
                                   */
                                  const currentValues =
                                    form.getState().values as PageData;

                                  form.reset({
                                    ...(PHIPhysicalToPageData(
                                      NoExaminedPhysical
                                    ) as PageData),
                                    examination:
                                      currentValues.examination ??
                                      null,
                                    examinationState:
                                      currentValues.examinationState ??
                                      null,
                                  } as PageData);
                                }

                                return nextExamine;
                              });
                            }}
                          >
                            <Dropdown.Item eventKey={0}>
                              否
                            </Dropdown.Item>

                            <Dropdown.Item eventKey={1}>
                              是
                            </Dropdown.Item>
                          </DropdownButton>
                        </Col>

                        {!examined && (
                          <Col xs={12} md={4}>
                            <div className="d-flex align-items-start">
                              <span
                                className="mr-2 text-danger"
                                style={{
                                  whiteSpace: 'nowrap',
                                  paddingTop: '7px',
                                }}
                              >
                                未受檢原因：
                              </span>

                              <ExaminationReasonField enabled={true} />
                            </div>
                          </Col>
                        )}

                        <Col xs={12} className="mt-2">
                          若是否受檢選擇&quot;否&quot;，
                          則存檔後系統會自動將下列各項目值設定為
                          9（未受檢）
                        </Col>
                      </Row>
                    </td>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row" className="align-middle">
                      血壓
                      <br />
                      腰圍
                    </th>
                    <td className="align-middle">
                      <Row noGutters className="align-items-center">
                        <div className="d-flex align-items-center px-2 my-1">
                          <span className="mx-1">收縮壓：</span>
                          <TextField
                            property="sbp"
                            className="rwid-750"
                            disabled={!examined}
                          />
                        </div>
                        <div className="d-flex align-items-center px-2 my-1">
                          <span className="mx-1">舒張壓：</span>
                          <TextField
                            property="dbp"
                            className="rwid-750"
                            disabled={!examined}
                          />
                          <InputDropdownGroupField
                            property="bpResult"
                            content={I18N.ExaminationResult}
                            type="number"
                            className="rwid-250"
                            disabled={!examined}
                            needGroup={false}
                          />
                          <span className="mx-1">血壓判讀</span>
                        </div>
                        <div className="d-flex align-items-center px-2 my-1">
                          <span className="mx-1">腰圍：</span>
                          <TextField
                            property="waistline"
                            className="rwid-750"
                            disabled={!examined}
                          />
                        </div>
                        <div className="d-flex align-items-center px-2 my-1">
                          <span className="mx-1">脈博：</span>
                          <TextField
                            property="pulse"
                            className="rwid-750"
                            disabled={!examined}
                          />
                          <InputDropdownGroupField
                            property="pulseResult"
                            content={I18N.ExaminationResult}
                            type="number"
                            className="rwid-250"
                            disabled={!examined}
                            needGroup={false}
                          />
                          <span className="mx-1">脈搏判讀</span>
                        </div>
                      </Row>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" className="align-middle">
                      眼
                    </th>
                    <td className="align-middle">
                      <Row noGutters className="align-items-center">
                        {generateExaminedOnly(
                          '辨色力異常',
                          'colorBlind',
                          !examined
                        )}
                        {generateExaminedAndSubValue(
                          '斜視',
                          'strabismus',
                          'strabismusType',
                          I18N.StrabismusResult,
                          !examined
                        )}
                        {generateExaminedOnly(
                          '睫毛倒插',
                          'trichiasis',
                          !examined
                        )}
                        {generateExaminedOnly(
                          '眼球震顫',
                          'nystagmus',
                          !examined
                        )}
                        {generateExaminedOnly(
                          '眼瞼下垂',
                          'blepharoptosis',
                          !examined
                        )}
                        {generate99state('e99', 'e99State', !examined)}
                      </Row>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" className="align-middle">
                      耳鼻喉
                    </th>
                    <td className="align-middle">
                      <Row noGutters className="align-items-center">
                        {generateExaminedAndSubValue(
                          '聽力異常',
                          'hearing',
                          'hearingLR',
                          I18N.LeftRightResult,
                          !examined,
                          'rwid-800'
                        )}
                        {generateExaminedAndSubValue(
                          '耳膜破損',
                          'eardrum',
                          'eardrumLR',
                          I18N.LeftRightResult,
                          !examined,
                          'rwid-800'
                        )}
                        {generateExaminedAndSubValue(
                          '耳道畸型',
                          'earMisshapen',
                          'earMisshapenLR',
                          I18N.LeftRightResult,
                          !examined,
                          'rwid-800'
                        )}
                        {generateExaminedOnly('唇顎裂', 'clp', !examined)}
                        {generateExaminedOnly(
                          '構音異常',
                          'articulationDisorders',
                          !examined
                        )}
                        {generateExaminedAndSubValue(
                          '耳前瘻管',
                          'preauricularFistula',
                          'preauricularFistulaLR',
                          I18N.LeftRightResult,
                          !examined,
                          'rwid-800'
                        )}
                        {generateExaminedAndSubValue(
                          '耵聹栓塞',
                          'cerumen',
                          'cerumenLR',
                          I18N.LeftRightResult,
                          !examined,
                          'rwid-800'
                        )}
                        {generateExaminedOnly(
                          '慢性鼻炎',
                          'rhinitis',
                          !examined
                        )}
                        {generateExaminedOnly(
                          '過敏性鼻炎',
                          'allergicRhinitis',
                          !examined
                        )}
                        {generateExaminedOnly(
                          '扁桃腺腫大',
                          'tonsillitis',
                          !examined
                        )}
                        {generate99state('o99', 'o99State', !examined)}
                      </Row>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" className="align-middle">
                      頭頸
                    </th>
                    <td className="align-middle">
                      <Row noGutters className="align-items-center">
                        {generateExaminedOnly('斜頸', 'torticollis', !examined)}
                        {generateExaminedOnly('異常腫塊', 'mass', !examined)}
                        {generateExaminedOnly('甲狀腺腫', 'goiter', !examined)}
                        {generateExaminedOnly(
                          '淋巴腺腫大',
                          'lymphadenectasis',
                          !examined
                        )}
                        {generateExaminedOnly(
                          '其他異常腫塊',
                          'mass99',
                          !examined
                        )}
                        {generate99state('n99', 'n99State', !examined)}
                      </Row>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" className="align-middle">
                      胸部
                    </th>
                    <td className="align-middle">
                      <Row noGutters className="align-items-center">
                        {generateExaminedOnly(
                          '心肺疾病',
                          'cardiopulmonary',
                          !examined
                        )}
                        {generateExaminedOnly(
                          '心雜音',
                          'heartMurmur',
                          !examined
                        )}
                        {generateExaminedOnly(
                          '心律不整',
                          'arrhythmia',
                          !examined
                        )}
                        {generateExaminedOnly(
                          '呼吸聲異常',
                          'wheeze',
                          !examined
                        )}
                        {generateExaminedOnly(
                          '其他心肺疾病',
                          'cardiopulmonary99',
                          !examined
                        )}
                        {generateExaminedOnly('胸廓異常', 'thorax', !examined)}
                        {generate99state('c99', 'c99State', !examined)}
                      </Row>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" className="align-middle">
                      腹部
                    </th>
                    <td className="align-middle">
                      <Row noGutters className="align-items-center">
                        {generateExaminedOnly(
                          '腹部異常腫大',
                          'abdomen',
                          !examined
                        )}
                        {generateExaminedOnly('疝氣', 'hernia', !examined)}
                        {generate99state('a99', 'a99State', !examined)}
                      </Row>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" className="align-middle">
                      脊柱
                      <br />
                      四肢
                    </th>
                    <td className="align-middle">
                      <Row noGutters className="align-items-center">
                        {generateExaminedOnly(
                          '脊柱側彎',
                          'scoliosis',
                          !examined
                        )}
                        {generateExaminedOnly(
                          '肢體畸形',
                          'dysmelia',
                          !examined
                        )}
                        {generateExaminedOnly(
                          '多併指（趾）',
                          'polydactyly',
                          !examined
                        )}
                        {generateExaminedOnly(
                          '關節變形',
                          'dysarthrosis',
                          !examined
                        )}
                        {generateExaminedOnly(
                          '其他肢體畸形',
                          'dysmelia99',
                          !examined
                        )}
                        {generateExaminedOnly(
                          '蹲踞困難',
                          'squatting',
                          !examined
                        )}
                        {generateExaminedOnly('水腫', 'edema', !examined)}
                        {generate99state('l99', 'l99State', !examined)}
                      </Row>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" className="align-middle">
                      泌尿
                      <br />
                      生殖
                    </th>
                    <td className="align-middle">
                      <Row
                        noGutters
                        className={`align-items-center ${student.sex === '2' ? 'd-none' : ''
                          }`}
                      >
                        {generateExaminedOnly(
                          '隱睪',
                          'cryptorchidism',
                          !examined
                        )}
                        {generateExaminedOnly(
                          '陰囊腫大',
                          'scrotalSwelling',
                          !examined
                        )}
                        {generateExaminedOnly('包皮異常', 'prepuce', !examined)}
                        {generateExaminedOnly(
                          '精索靜脈曲張',
                          'varicocele',
                          !examined
                        )}
                        {generate99state('u99', 'u99State', !examined)}
                      </Row>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" className="align-middle">
                      皮膚
                    </th>
                    <td className="align-middle">
                      <Row noGutters className="align-items-center">
                        {generateExaminedOnly(
                          '癬',
                          'epidermophytosis',
                          !examined
                        )}
                        {generateExaminedOnly('疣', 'wart', !examined)}
                        {generateExaminedOnly('紫斑', 'purpura', !examined)}
                        {generateExaminedOnly('疥瘡', 'scabies', !examined)}
                        {generateExaminedOnly('溼疹', 'eczema', !examined)}
                        {generateExaminedOnly(
                          '異位性皮膚炎',
                          'atopicDermatitis',
                          !examined
                        )}
                        {generate99state('d99', 'd99State', !examined)}
                      </Row>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" className="align-middle">
                      口腔
                      <br />
                      初檢
                    </th>
                    <td className="align-middle">
                      <Row noGutters className="align-items-center">
                        {generateExaminedOnly(
                          '未治療齲齒',
                          't01',
                          !examined,
                          I18N.SingleExaminationOnlyResult
                        )}
                        {examined &&
                          Number(values.t01) ===
                          Number(
                            TeeInitialExaminationEnum.NoExamined
                          ) && (
                            <div
                              className="d-flex align-items-start px-2 my-2"
                              style={{
                                width: '100%',
                              }}
                            >
                              <span
                                className="mr-2 text-danger"
                                style={{
                                  whiteSpace: 'nowrap',
                                  paddingTop: '7px',
                                }}
                              >
                                未受檢原因：
                              </span>

                              <ExaminationReasonField type="tee" enabled={true} />
                            </div>
                          )}
                        {generateExaminedOnly(
                          '已治療齲齒',
                          't11',
                          !examined,
                          I18N.TreatedCariesExaminationOnlyResult
                        )}
                        {generateExaminedOnly(
                          '上顎恆牙第一大臼齒齲齒經驗',
                          't12',
                          !examined,
                          I18N.TreatedCariesExaminationOnlyResult
                        )}
                        {generateExaminedOnly(
                          '下顎恆牙第一大臼齒齲齒經驗',
                          't13',
                          !examined,
                          I18N.TreatedCariesExaminationOnlyResult
                        )}

                        {generateExaminedOnly(
                          '恆牙臼齒窩溝封填',
                          't15',
                          !examined,
                          I18N.TreatedCariesExaminationOnlyResult
                        )}
                        {generateExaminedOnly(
                          '口腔衛生不良',
                          't04',
                          !examined,
                          I18N.SingleExaminationOnlyResult
                        )}
                        {generateExaminedOnly(
                          '牙結石',
                          't05',
                          !examined,
                          I18N.SingleExaminationOnlyResult
                        )}
                        {generateExaminedOnly(
                          '牙齦炎',
                          't08',
                          !examined,
                          I18N.SingleExaminationOnlyResult
                        )}
                        {generateExaminedOnly(
                          '咬合不正',
                          't07',
                          !examined,
                          I18N.SingleExaminationOnlyResult
                        )}
                        {generateExaminedOnly(
                          '口腔黏膜異常',
                          't09',
                          !examined,
                          I18N.SingleExaminationOnlyResult
                        )}

                        {generateExaminedOnly(
                          '牙周病',
                          't16',
                          !examined,
                          I18N.SingleExaminationOnlyResult
                        )}
                        {generateExaminedOnly(
                          '乳牙待拔牙',
                          't17',
                          !examined,
                          I18N.SingleExaminationOnlyResult
                        )}
                        {generateExaminedOnly(
                          '待拔牙',
                          't03',
                          !examined,
                          I18N.SingleExaminationOnlyResult
                        )}
                        {generateExaminedOnly(
                          '贅生牙',
                          't18',
                          !examined,
                          I18N.SingleExaminationOnlyResult
                        )}
                        {generateExaminedOnly(
                          '缺牙',
                          't02',
                          !examined,
                          I18N.SingleExaminationOnlyResult
                        )}
                        {generateExaminedOnly(
                          '阻生牙',
                          't19',
                          !examined,
                          I18N.SingleExaminationOnlyResult
                        )}

                        {generate99state(
                          't99',
                          't99state',
                          !examined,
                          I18N.SingleExaminationOnlyResult
                        )}

                        <div className="d-flex align-items-center px-2 my-1">
                          <div className="mr-1">
                            口檢表
                            <span className="mx-1 text-danger">(非必填)</span>
                          </div>
                          <div className="rwid-1875">
                            <TeeSheetWithButton disabled={!examined} />
                          </div>
                        </div>
                      </Row>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" className="align-middle">
                      口腔
                      <br />
                      複檢
                    </th>
                    <td className="align-middle">
                      <Row noGutters className="align-items-center">
                        {generateExaminedOnly(
                          '未治療齲齒',
                          'ret01',
                          (v) =>
                            !(
                              examined &&
                              v.t01 != TeeInitialExaminationEnum.NoExamined &&
                              v.t01 != TeeInitialExaminationEnum.NoProblem
                            ),
                          I18N.FollowUpExaminationResult
                        )}
                        {generateExaminedOnly(
                          '口腔衛生不良',
                          'ret04',
                          (v) =>
                            !(
                              examined &&
                              v.t04 != TeeInitialExaminationEnum.NoExamined &&
                              v.t04 != TeeInitialExaminationEnum.NoProblem
                            ),
                          I18N.FollowUpExaminationResult
                        )}
                        {generateExaminedOnly(
                          '牙結石',
                          'ret05',
                          (v) =>
                            !(
                              examined &&
                              v.t05 != TeeInitialExaminationEnum.NoExamined &&
                              v.t05 != TeeInitialExaminationEnum.NoProblem
                            ),
                          I18N.FollowUpExaminationResult
                        )}
                        {generateExaminedOnly(
                          '牙齦炎',
                          'ret08',
                          (v) =>
                            !(
                              examined &&
                              v.t08 != TeeInitialExaminationEnum.NoExamined &&
                              v.t08 != TeeInitialExaminationEnum.NoProblem
                            ),
                          I18N.FollowUpExaminationResult
                        )}
                        {generateExaminedOnly(
                          '咬合不正',
                          'ret07',
                          (v) =>
                            !(
                              examined &&
                              v.t07 != TeeInitialExaminationEnum.NoExamined &&
                              v.t07 != TeeInitialExaminationEnum.NoProblem
                            ),
                          I18N.FollowUpExaminationResult
                        )}
                        {generateExaminedOnly(
                          '口腔黏膜異常',
                          'ret09',
                          (v) =>
                            !(
                              examined &&
                              v.t09 != TeeInitialExaminationEnum.NoExamined &&
                              v.t09 != TeeInitialExaminationEnum.NoProblem
                            ),
                          I18N.FollowUpExaminationResult
                        )}

                        {generateExaminedOnly(
                          '牙周病',
                          'ret16',
                          (v) =>
                            !(
                              examined &&
                              v.t16 != TeeInitialExaminationEnum.NoExamined &&
                              v.t16 != TeeInitialExaminationEnum.NoProblem
                            ),
                          I18N.FollowUpExaminationResult
                        )}
                        {generateExaminedOnly(
                          '乳牙待拔牙',
                          'ret17',
                          (v) =>
                            !(
                              examined &&
                              v.t17 != TeeInitialExaminationEnum.NoExamined &&
                              v.t17 != TeeInitialExaminationEnum.NoProblem
                            ),
                          I18N.FollowUpExaminationResult
                        )}
                        {generateExaminedOnly(
                          '待拔牙',
                          'ret03',
                          (v) =>
                            !(
                              examined &&
                              v.t03 != TeeInitialExaminationEnum.NoExamined &&
                              v.t03 != TeeInitialExaminationEnum.NoProblem
                            ),
                          I18N.FollowUpExaminationResult
                        )}
                        {generateExaminedOnly(
                          '贅生牙',
                          'ret18',
                          (v) =>
                            !(
                              examined &&
                              v.t18 != TeeInitialExaminationEnum.NoExamined &&
                              v.t18 != TeeInitialExaminationEnum.NoProblem
                            ),
                          I18N.FollowUpExaminationResult
                        )}
                        {generateExaminedOnly(
                          '缺牙',
                          'ret02',
                          (v) =>
                            !(
                              examined &&
                              v.t02 != TeeInitialExaminationEnum.NoExamined &&
                              v.t02 != TeeInitialExaminationEnum.NoProblem
                            ),
                          I18N.FollowUpExaminationResult
                        )}
                        {generateExaminedOnly(
                          '阻生牙',
                          'ret19',
                          (v) =>
                            !(
                              examined &&
                              v.t19 != TeeInitialExaminationEnum.NoExamined &&
                              v.t19 != TeeInitialExaminationEnum.NoProblem
                            ),
                          I18N.FollowUpExaminationResult
                        )}
                        {generate99state(
                          'ret99',
                          'reT99state',
                          (v) =>
                            !(
                              examined &&
                              v.t99 != TeeInitialExaminationEnum.NoExamined &&
                              v.t99 != TeeInitialExaminationEnum.NoProblem
                            ),
                          I18N.FollowUpExaminationResult
                        )}

                        <div className="d-flex align-items-center px-2 my-1 ">
                          <div className="mr-1">
                            複檢口檢表
                            <span className="mx-1 text-danger">(非必填)</span>
                          </div>
                          <div className="rwid-1875">
                            <TeeSheetWithButton
                              property="reTSheet"
                              disabled={!examined}
                            />
                          </div>
                        </div>
                      </Row>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" className="align-middle">
                      檢查
                      <br />
                      單位
                    </th>
                    <td className="align-middle">
                      <Row noGutters className="align-items-center">
                        <div className="d-flex align-items-center px-2 my-1">
                          檢查單位：
                          <TextField
                            property="checkUnit"
                            disabled={!examined}
                            className="ml-1 rwid-1250"
                          />
                        </div>
                        <div className="d-flex align-items-center px-2 my-1">
                          醫師：
                          <TextField
                            property="doctor"
                            disabled={!examined}
                            className="ml-1 rwid-1250"
                          />
                        </div>
                        <div className="d-flex align-items-center px-2 my-1">
                          牙醫師：
                          <TextField
                            property="teeDoctor"
                            disabled={!examined}
                            className="ml-1 rwid-1250"
                          />
                        </div>
                        <div className="d-flex align-items-center px-2 my-1">
                          檢查日期：
                          <DateTimeField
                            property="examDate"
                            disabled={!examined}
                            className="ml-1 rwid-1250"
                          // end={DateTime.now()}
                          />
                        </div>
                      </Row>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2} className="align-middle">
                      <Button
                        className="mx-2"
                        onClick={() => {
                          handleSubmit();
                        }}
                      >
                        <span className="feather icon-check mr-1" />
                        儲存
                      </Button>
                      <Button
                        className="mx-2"
                        variant="danger"
                        onClick={onClose}
                      >
                        <span className="feather icon-x  mr-1" />
                        取消
                      </Button>
                    </td>
                  </tr>
                </tbody>
              </Table>
            );
          }}
        />
      </Modal.Body>
    </Modal>
  );
}

function isPhysicalExamined(
  physical?: PageData
): boolean {
  if (!physical) {
    return true;
  }

  const values = [
    physical.bpResult,
    physical.pulseResult,
    physical.colorBlind,
    physical.hearing,
    physical.torticollis,
    physical.cardiopulmonary,
    physical.abdomen,
    physical.scoliosis,
    physical.epidermophytosis,
  ];

  const isAllNoExamined = values.every(
    (value) =>
      Number(value) ===
      Number(ExaminedResultEnum.NoExamined)
  );

  return !isAllNoExamined;
}

type ModelManageProps = {
  inputing: boolean;
  physical?: PageData;
  student: StudentPHIAll;
  grade: number;
  onClose: () => void;
  onValueUpdate: (v: PageData) => void;
};
function PhysicalManageModel({
  inputing,
  physical,
  student,
  grade,
  onClose,
  onValueUpdate,
}: ModelManageProps): JSX.Element {
  return (
    <Modal
      show={inputing}
      aria-labelledby="contained-modal-title-vcenter"
      backdrop="static"
      dialogClassName="modal-dialog-full"
      centered
    >
      <Modal.Header closeButton onHide={onClose}>
        <Modal.Title id="contained-modal-title-vcenter">
          健康管理 -{' '}
          <strong>
            {student.seat} {student.name}
          </strong>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-auto">
        <Form
          initialValues={physical ?? PHIPhysicalToPageData(NoExaminedPhysical)}
          decorators={PhiysicalTeeCalculators}
          onSubmit={(v: PageData) => {
            onValueUpdate(v);
          }}
          validateOnBlur
          subscription={{}}
          render={({ handleSubmit, form }) => {
            const values = form.getState().values;
            const { subjects } = form.getState().values;
            const rowClassName = 'align-items-center my-1';
            return (
              <Row noGutters>
                <Col sm={5} className="px-2">
                  <Table bordered>
                    <colgroup>
                      <col width={'20%'} />
                      <col width={'80%'} />
                    </colgroup>
                    <tbody>
                      <tr>
                        <td>學期/日期</td>
                        <td>
                          {values
                            ? values.examDate?.toFormat('yyyy/MM/dd') ??
                            '無檢查日期'
                            : '尚未受檢'}
                        </td>
                      </tr>
                      {generatePhysicalSummarize(values)}
                    </tbody>
                  </Table>
                </Col>
                <Col sm={7} className="px-2">
                  <Table striped bordered hover className="text-break">
                    <colgroup>
                      <col width={'7%'} />
                      <col width={'90%'} />
                    </colgroup>
                    <thead>
                      <tr>
                        <td className="align-middle">項目</td>
                        <td className="align-middle">
                          <Row noGutters className={rowClassName}>
                            <span className="mr-1">身分證：</span>
                            <b className="mr-2">{student.pid}</b>
                            <span className="mr-1">檢查年級：</span>
                            <b className="mr-2">{grade}</b>
                          </Row>
                        </td>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>總評</td>
                        <td>
                          <Row noGutters className={rowClassName}>
                            {generateCheckedBox('有異狀', 'someProblem')}
                            <span className="mr-2">需就診科別:</span>
                            <DropdownCheckListField
                              property="subjects"
                              selectShow="value"
                              content={I18N.PhysicalManageSubjects}
                              deserialize={(v) => v.split(',')}
                              serialize={(v) => v.join(',')}
                            />
                          </Row>
                        </td>
                      </tr>
                      <tr>
                        <td>健康管理</td>
                        <td>
                          <Row noGutters className={rowClassName}>
                            {generateCheckedBox('已就醫', 'finished')}
                            <span className="mr-2">科別:</span>
                            <DropdownCheckListField
                              property="finishSubjects"
                              selectShow="value"
                              content={(subjects ?? []).reduce(
                                (acc, cur) => ({ ...acc, [cur]: cur }),
                                {}
                              )}
                              deserialize={(v) => v.split(',')}
                              serialize={(v) => v.join(',')}
                            />
                          </Row>
                          <Row noGutters className={rowClassName}>
                            {generateCheckedBox('需持續追蹤', 'trace')}
                            <span className="mr-2">項目:</span>
                            <DropdownCheckListField
                              property="traceItems"
                              selectShow="value"
                              content={ManageTraceKey.reduce((acc, cur) => {
                                if (values[cur] === 1)
                                  return {
                                    ...acc,
                                    [I18N.Physical[cur]]: I18N.Physical[cur],
                                  };
                                return acc;
                              }, {})}
                              deserialize={(v) => v.split(',')}
                              serialize={(v) => v.join(',')}
                            />
                          </Row>
                          <Row noGutters className={rowClassName}>
                            {generateCheckedBox('需限制體能活動', 'limit')}
                            {generateCheckedBox('需配輔助器材', 'assist')}
                          </Row>
                          <Row noGutters className={rowClassName}>
                            {generateCheckedBox(
                              '宜在特殊學校或特殊班就讀',
                              'special'
                            )}
                          </Row>
                          <MyInputGroup className={rowClassName}>
                            <span className="mr-2">註解:</span>
                            <TextField
                              property="memos"
                              className="flex-grow-1"
                            />
                          </MyInputGroup>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={2} className="align-middle">
                          <Button
                            className="mx-2"
                            onClick={() => {
                              handleSubmit();
                            }}
                          >
                            <span className="feather icon-check mr-1" />
                            儲存
                          </Button>
                          <Button
                            className="mx-2"
                            variant="danger"
                            onClick={onClose}
                          >
                            <span className="feather icon-x  mr-1" />
                            取消
                          </Button>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
              </Row>
            );
          }}
        />
      </Modal.Body>
    </Modal>
  );
}

function generateExaminedAndSubValue(
  display: string,
  property: string,
  subProperty: string,
  subValues: Record<number, string>,
  disabled: boolean,
  subInputClassName: string = 'rwid-750'
): ReactNode {
  return (
    <div className="d-flex align-items-center px-2 my-1">
      <InputDropdownGroupField
        className={`rwid-250 ml-1`}
        property={property}
        content={I18N.ExaminationResult}
        type="number"
        disabled={disabled}
        maxLength={1}
        needGroup={false}
      />
      <span className="mx-1">{display}</span>
      <InputDropdownGroupField
        resultClassName={`${subInputClassName} mr-1`}
        className="rwid-250"
        property={subProperty}
        content={subValues}
        type="number"
        disabled={disabled}
        resultShow="both"
        needGroup={false}
      />
    </div>
  );
}

function generateCheckedBox(display: string, property: string): ReactNode {
  return (
    <MyInputGroup>
      <CheckBoxField property={property} label={display} />
    </MyInputGroup>
  );
}

export function invalidStatus(
  data: ExaminedResultEnum | TeeInspection
): boolean {
  return data === 1 || data === 2 || data === 3;
}

function generatePhysicalSummarize(pageData: PageData): ReactNode {
  const nodes: ReactNode[] = [];
  const bpNo = BPKey.filter((v) => invalidStatus(pageData[v]));
  if (bpNo.length > 0) {
    nodes.push(
      <tr key={'bp'}>
        <td>血壓</td>
        <td>
          {bpNo.map((v) => {
            return generateUnhealthy(
              v,
              pageData[v],
              unhealthySubType(pageData, v)
            );
          })}
        </td>
      </tr>
    );
  }
  const eyeNo = EyeKey.filter((v) => invalidStatus(pageData[v]));
  if (eyeNo.length > 0) {
    nodes.push(
      <tr key={'eye'}>
        <td>眼</td>
        <td>
          {eyeNo.map((v) => {
            return generateUnhealthy(
              v,
              pageData[v],
              unhealthySubType(pageData, v)
            );
          })}
        </td>
      </tr>
    );
  }

  const entNo = ENTKey.filter((v) => invalidStatus(pageData[v]));
  if (entNo.length > 0) {
    nodes.push(
      <tr key={'ent'}>
        <td>耳鼻喉</td>
        <td>
          {entNo.map((v) => {
            return generateUnhealthy(
              v,
              pageData[v],
              unhealthySubType(pageData, v)
            );
          })}
        </td>
      </tr>
    );
  }

  const neckNo = NeckKey.filter((v) => invalidStatus(pageData[v]));
  if (neckNo.length > 0) {
    nodes.push(
      <tr key={'neck'}>
        <td>頭頸</td>
        <td>
          {neckNo.map((v) => {
            return generateUnhealthy(
              v,
              pageData[v],
              unhealthySubType(pageData, v)
            );
          })}
        </td>
      </tr>
    );
  }

  const chestNo = ChestKey.filter((v) => invalidStatus(pageData[v]));
  if (chestNo.length > 0) {
    nodes.push(
      <tr key={'chest'}>
        <td>胸部</td>
        <td>
          {chestNo.map((v) => {
            return generateUnhealthy(
              v,
              pageData[v],
              unhealthySubType(pageData, v)
            );
          })}
        </td>
      </tr>
    );
  }

  const abdomenNo = AbdomenKey.filter((v) => invalidStatus(pageData[v]));
  if (abdomenNo.length > 0) {
    nodes.push(
      <tr key={'abdomen'}>
        <td>腹部</td>
        <td>
          {abdomenNo.map((v) => {
            return generateUnhealthy(
              v,
              pageData[v],
              unhealthySubType(pageData, v)
            );
          })}
        </td>
      </tr>
    );
  }

  const spineNo = SpineKey.filter((v) => invalidStatus(pageData[v]));
  if (spineNo.length > 0) {
    nodes.push(
      <tr key={'spine'}>
        <td>脊柱四肢</td>
        <td>
          {spineNo.map((v) => {
            return generateUnhealthy(
              v,
              pageData[v],
              unhealthySubType(pageData, v)
            );
          })}
        </td>
      </tr>
    );
  }

  const urogenitalNo = UrogenitalKey.filter((v) => invalidStatus(pageData[v]));
  if (urogenitalNo.length > 0) {
    nodes.push(
      <tr key={'urogenital'}>
        <td>泌尿生殖</td>
        <td>
          {urogenitalNo.map((v) => {
            return generateUnhealthy(
              v,
              pageData[v],
              unhealthySubType(pageData, v)
            );
          })}
        </td>
      </tr>
    );
  }

  const skinNo = SkinKey.filter((v) => invalidStatus(pageData[v]));
  if (skinNo.length > 0) {
    nodes.push(
      <tr key={'skin'}>
        <td>皮膚</td>
        <td>
          {skinNo.map((v) => {
            return generateUnhealthy(
              v,
              pageData[v],
              unhealthySubType(pageData, v)
            );
          })}
        </td>
      </tr>
    );
  }

  const oralNo = OralStatusKey.map((v) => {
    const isRegularKey = OralRegularKey.filter((s) => s === v).length > 0;
    const value = isRegularKey
      ? pageData[v as (typeof OralRegularKey)[number]]
      : TeeToExaminedEnum(
        pageData[v as (typeof OralFollowKey)[number]],
        pageData[`re${v as (typeof OralFollowKey)[number]}`]
      );
    const status = invalidStatus(value);
    return {
      key: v,
      value,
      invalidStatus: status,
    };
  }).filter((v) => v.invalidStatus);

  if (oralNo.length > 0) {
    nodes.push(
      <tr key={'oral'}>
        <td>口腔</td>
        <td>
          {oralNo.map((v) => {
            return generateUnhealthy(
              v.key,
              v.value,
              unhealthySubType(pageData, v.key)
            );
          })}
        </td>
      </tr>
    );
  }

  const manageNo = ManageKey.filter((v) => pageData[v]);

  if (manageNo.length > 0) {
    nodes.push(
      <tr key={'manage'}>
        <td>健康管理</td>
        <td>{manageNo.map((v) => generateUnhealthyManage(pageData, v))}</td>
      </tr>
    );
  }

  return nodes;
}

function generateUnhealthy(
  property: keyof PageData,
  value: ExaminedResultEnum | TeeInspection,
  subType: string | undefined | null
): ReactNode {
  return (
    <div key={property}>
      <span className="text-danger">*{I18N.Physical[property]}</span>:{value}
      {subType ? `-${subType}` : ''}
    </div>
  );
}

function generateUnhealthyManage(
  pageData: PageData,
  property: keyof PageData
): ReactNode {
  const successStyle = ['finished', 'memos'].includes(property)
    ? 'text-primary'
    : 'text-danger';
  const subtype = unhealthySubType(pageData, property);
  return (
    <div key={property}>
      <span className={successStyle}>*{I18N.Physical[property]}</span>
      <br />
      {subtype ? `-${subtype}` : ''}
    </div>
  );
}

function generatePhysical(grade: number, sex: string): PHIPhysical {
  const defaultPhysical = generateDefaultPhysical(grade);
  return {
    ...defaultPhysical,
    ...(sex === '2'
      ? {
        cryptorchidism: ExaminedResultEnum.NoExamined,
        prepuce: ExaminedResultEnum.NoExamined,
        varicocele: ExaminedResultEnum.NoExamined,
        u99: ExaminedResultEnum.NoExamined,
        scrotalSwelling: ExaminedResultEnum.NoExamined,
      }
      : {}),
  };
}

function unhealthySubType(
  data: PageData,
  property: keyof PageData
): string | null | undefined {
  switch (property) {
    case 'strabismus':
      return I18N.StrabismusResult[data.strabismusType];
    case 'e99':
      return data.e99State;
    case 'hearing':
      return I18N.LeftRightResult[data.hearingLR];
    case 'eardrum':
      return I18N.LeftRightResult[data.eardrumLR];
    case 'earMisshapen':
      return I18N.LeftRightResult[data.earMisshapenLR];
    case 'preauricularFistula':
      return I18N.LeftRightResult[data.preauricularFistulaLR];
    case 'cerumen':
      return I18N.LeftRightResult[data.cerumenLR];
    case 'o99':
      return data.o99State;
    case 'n99':
      return data.n99State;
    case 'c99':
      return data.c99State;
    case 'a99':
      return data.a99State;
    case 'l99':
      return data.l99State;
    case 'u99':
      return data.u99State;
    case 'd99':
      return data.d99State;
    case 't99':
      return data.t99state;
    case 'someProblem':
      return data.subjects.join(',');
    case 'finished':
      return data.finishSubjects.join(',');
    case 'trace':
      return data.traceItems.join(',');
    case 'memos':
      return data.memos;
    default:
      return undefined;
  }
}

function generateDefaultPhysical(grade: number): PHIPhysical {
  switch (grade) {
    case 1:
      return {
        ...DefaultPhysical,
        // eye
        trichiasis: ExaminedResultEnum.NoProblem,

        // Ear, Nose, and Throat
        eardrum: ExaminedResultEnum.NoProblem,
        earMisshapen: ExaminedResultEnum.NoProblem,
        cerumen: ExaminedResultEnum.NoProblem,
        tonsillitis: ExaminedResultEnum.NoProblem,

        cryptorchidism: ExaminedResultEnum.NoProblem,
        t15: TeeInspection.None,
      };
    case 4:
      return {
        ...DefaultPhysical,
        trichiasis: ExaminedResultEnum.NoProblem,

        // Ear, Nose, and Throat
        eardrum: ExaminedResultEnum.NoProblem,
        earMisshapen: ExaminedResultEnum.NoProblem,
        cerumen: ExaminedResultEnum.NoProblem,
        tonsillitis: ExaminedResultEnum.NoProblem,
        t15: TeeInspection.None,
      };
    case 7:
      return {
        ...DefaultPhysical,
        strabismus: ExaminedResultEnum.NoExamined,
        t09: ExaminedResultEnum.NoProblem,
      };
    case 10:
      return {
        ...DefaultPhysical,
        strabismus: ExaminedResultEnum.NoExamined,
      };
    case 13:
      return {
        ...DefaultPhysical,
        bpResult: ExaminedResultEnum.NoProblem,
        pulseResult: ExaminedResultEnum.NoProblem,
        strabismus: ExaminedResultEnum.NoExamined,
        t09: ExaminedResultEnum.NoProblem,
      };
    default:
      return {
        ...DefaultPhysical,
        colorBlind: ExaminedResultEnum.NoExamined,
        e99: ExaminedResultEnum.NoExamined,

        hearing: ExaminedResultEnum.NoExamined,
        o99: ExaminedResultEnum.NoExamined,

        torticollis: ExaminedResultEnum.NoExamined,
        mass: ExaminedResultEnum.NoExamined,
        goiter: ExaminedResultEnum.NoExamined,
        lymphadenectasis: ExaminedResultEnum.NoExamined,
        mass99: ExaminedResultEnum.NoExamined,
        n99: ExaminedResultEnum.NoExamined,

        cardiopulmonary: ExaminedResultEnum.NoExamined,
        heartMurmur: ExaminedResultEnum.NoExamined,
        arrhythmia: ExaminedResultEnum.NoExamined,
        wheeze: ExaminedResultEnum.NoExamined,
        cardiopulmonary99: ExaminedResultEnum.NoExamined,
        thorax: ExaminedResultEnum.NoExamined,
        c99: ExaminedResultEnum.NoExamined,

        abdomen: ExaminedResultEnum.NoExamined,
        a99: ExaminedResultEnum.NoExamined,

        scoliosis: ExaminedResultEnum.NoExamined,
        dysmelia: ExaminedResultEnum.NoExamined,
        polydactyly: ExaminedResultEnum.NoExamined,
        dysarthrosis: ExaminedResultEnum.NoExamined,
        dysmelia99: ExaminedResultEnum.NoExamined,
        squatting: ExaminedResultEnum.NoExamined,
        l99: ExaminedResultEnum.NoExamined,

        prepuce: ExaminedResultEnum.NoExamined,
        varicocele: ExaminedResultEnum.NoExamined,
        u99: ExaminedResultEnum.NoExamined,

        epidermophytosis: ExaminedResultEnum.NoExamined,
        scabies: ExaminedResultEnum.NoExamined,
        wart: ExaminedResultEnum.NoExamined,
        atopicDermatitis: ExaminedResultEnum.NoExamined,
        eczema: ExaminedResultEnum.NoExamined,
        d99: ExaminedResultEnum.NoExamined,

        t01: ExaminedResultEnum.NoExamined,
        t04: ExaminedResultEnum.NoExamined,
        t05: ExaminedResultEnum.NoExamined,
        t08: ExaminedResultEnum.NoExamined,
        t07: ExaminedResultEnum.NoExamined,
        t99: ExaminedResultEnum.NoExamined,
      };
  }
}
export function PHIPhysicalToPageData(physical: PHIPhysical): PageData {
  return {
    ...physical,
    t01: ExaminedResultToTeeEnum(physical.t01),
    ret01: ExaminedResultToTeeFollowUpEnum(physical.t01),
    t02: ExaminedResultToTeeEnum(physical.t02),
    ret02: ExaminedResultToTeeFollowUpEnum(physical.t02),
    t03: ExaminedResultToTeeEnum(physical.t03),
    ret03: ExaminedResultToTeeFollowUpEnum(physical.t03),
    t04: ExaminedResultToTeeEnum(physical.t04),
    ret04: ExaminedResultToTeeFollowUpEnum(physical.t04),
    t05: ExaminedResultToTeeEnum(physical.t05),
    ret05: ExaminedResultToTeeFollowUpEnum(physical.t05),
    t07: ExaminedResultToTeeEnum(physical.t07),
    ret07: ExaminedResultToTeeFollowUpEnum(physical.t07),
    t08: ExaminedResultToTeeEnum(physical.t08),
    ret08: ExaminedResultToTeeFollowUpEnum(physical.t08),
    t99: ExaminedResultToTeeEnum(physical.t99),
    ret99: ExaminedResultToTeeFollowUpEnum(physical.t99),
    t09: ExaminedResultToTeeEnum(physical.t09),
    ret09: ExaminedResultToTeeFollowUpEnum(physical.t09),
    t16: ExaminedResultToTeeEnum(physical.t16),
    ret16: ExaminedResultToTeeFollowUpEnum(physical.t16),
    t17: ExaminedResultToTeeEnum(physical.t17),
    ret17: ExaminedResultToTeeFollowUpEnum(physical.t17),
    t18: ExaminedResultToTeeEnum(physical.t18),
    ret18: ExaminedResultToTeeFollowUpEnum(physical.t18),
    t19: ExaminedResultToTeeEnum(physical.t19),
    ret19: ExaminedResultToTeeFollowUpEnum(physical.t19),
  };
}

function PageDataToPHIPhysical({
  t01,
  ret01,
  t02,
  ret02,
  t03,
  ret03,
  t04,
  ret04,
  t05,
  ret05,
  t07,
  ret07,
  t08,
  ret08,
  t99,
  ret99,
  t09,
  ret09,
  t16,
  ret16,
  t17,
  ret17,
  t18,
  ret18,
  t19,
  ret19,
  bpResult,
  waistResult,
  pulseResult,
  colorBlind,
  e99,
  hearing,
  hearingLR,
  earMisshapen,
  earMisshapenLR,
  eardrum,
  eardrumLR,
  cerumen,
  cerumenLR,
  tonsillitis,
  o99,
  torticollis,
  mass,
  goiter,
  lymphadenectasis,
  mass99,
  n99,
  cardiopulmonary,
  heartMurmur,
  arrhythmia,
  wheeze,
  cardiopulmonary99,
  thorax,
  c99,
  abdomen,
  a99,
  scoliosis,
  dysmelia,
  polydactyly,
  dysarthrosis,
  dysmelia99,
  squatting,
  l99,
  cryptorchidism,
  prepuce,
  varicocele,
  u99,
  epidermophytosis,
  scabies,
  wart,
  atopicDermatitis,
  eczema,
  d99,
  t06,
  t11,
  t12,
  t13,
  t15,
  strabismus,
  strabismusType,
  trichiasis,
  nystagmus,
  blepharoptosis,
  clp,
  articulationDisorders,
  preauricularFistula,
  preauricularFistulaLR,
  rhinitis,
  allergicRhinitis,
  hernia,
  edema,
  scrotalSwelling,
  purpura,
  ...physical
}: PageData): PHIPhysical {
  return {
    ...physical,
    t01: TeeToExaminedEnum(t01, ret01),
    t02: TeeToExaminedEnum(t02, ret02),
    t03: TeeToExaminedEnum(t03, ret03),
    t04: TeeToExaminedEnum(t04, ret04),
    t05: TeeToExaminedEnum(t05, ret05),
    t07: TeeToExaminedEnum(t07, ret07),
    t08: TeeToExaminedEnum(t08, ret08),
    t99: TeeToExaminedEnum(t99, ret99),
    t09: TeeToExaminedEnum(t09, ret09),
    t16: TeeToExaminedEnum(t16, ret16),
    t17: TeeToExaminedEnum(t17, ret17),
    t18: TeeToExaminedEnum(t18, ret18),
    t19: TeeToExaminedEnum(t19, ret19),
    bpResult: +bpResult,
    waistResult: +waistResult,
    pulseResult: +pulseResult,
    colorBlind: +colorBlind,
    e99: +e99,
    hearing: +hearing,
    hearingLR: +hearingLR,
    earMisshapen: +earMisshapen,
    earMisshapenLR: +earMisshapenLR,
    eardrum: +eardrum,
    eardrumLR: +eardrumLR,
    cerumen: +cerumen,
    cerumenLR: +cerumenLR,
    tonsillitis: +tonsillitis,
    o99: +o99,
    torticollis: +torticollis,
    mass: +mass,
    goiter: +goiter,
    lymphadenectasis: +lymphadenectasis,
    mass99: +mass99,
    n99: +n99,
    cardiopulmonary: +cardiopulmonary,
    heartMurmur: +heartMurmur,
    arrhythmia: +arrhythmia,
    wheeze: +wheeze,
    cardiopulmonary99: +cardiopulmonary99,
    thorax: +thorax,
    c99: +c99,
    abdomen: +abdomen,
    a99: +a99,
    scoliosis: +scoliosis,
    dysmelia: +dysmelia,
    polydactyly: +polydactyly,
    dysarthrosis: +dysarthrosis,
    dysmelia99: +dysmelia99,
    squatting: +squatting,
    l99: +l99,
    cryptorchidism: +cryptorchidism,
    prepuce: +prepuce,
    varicocele: +varicocele,
    u99: +u99,
    epidermophytosis: +epidermophytosis,
    scabies: +scabies,
    wart: +wart,
    atopicDermatitis: +atopicDermatitis,
    eczema: +eczema,
    d99: +d99,
    t06: +t06,
    t11: +t11,
    t12: +t12,
    t13: +t13,
    t15: +t15,
    strabismus: +strabismus,
    strabismusType: +strabismusType,
    trichiasis: +trichiasis,
    nystagmus: +nystagmus,
    blepharoptosis: +blepharoptosis,
    clp: +clp,
    articulationDisorders: +articulationDisorders,
    preauricularFistula: +preauricularFistula,
    preauricularFistulaLR: +preauricularFistulaLR,
    rhinitis: +rhinitis,
    allergicRhinitis: +allergicRhinitis,
    hernia: +hernia,
    edema: +edema,
    scrotalSwelling: +scrotalSwelling,
    purpura: +purpura,
  };
}

const DefaultPhysical: PHIPhysical = {
  year: 0,
  sem: 0,
  grade: 0,
  yearClassId: 0,
  id: 0,
  bpResult: ExaminedResultEnum.NoExamined,
  waistResult: ExaminedResultEnum.NoExamined,
  pulseResult: ExaminedResultEnum.NoExamined,
  colorBlind: ExaminedResultEnum.NoProblem,
  e99: ExaminedResultEnum.NoProblem,
  hearing: ExaminedResultEnum.NoProblem,
  hearingLR: LeftRightEnum.None,
  earMisshapen: ExaminedResultEnum.NoExamined,
  earMisshapenLR: LeftRightEnum.None,
  eardrum: ExaminedResultEnum.NoExamined,
  eardrumLR: LeftRightEnum.None,
  cerumen: ExaminedResultEnum.NoExamined,
  cerumenLR: LeftRightEnum.None,
  tonsillitis: ExaminedResultEnum.NoExamined,
  o99: ExaminedResultEnum.NoProblem,
  torticollis: ExaminedResultEnum.NoProblem,
  mass: ExaminedResultEnum.NoProblem,
  goiter: ExaminedResultEnum.NoProblem,
  lymphadenectasis: ExaminedResultEnum.NoProblem,
  mass99: ExaminedResultEnum.NoProblem,
  n99: ExaminedResultEnum.NoProblem,
  cardiopulmonary: ExaminedResultEnum.NoProblem,
  heartMurmur: ExaminedResultEnum.NoProblem,
  arrhythmia: ExaminedResultEnum.NoProblem,
  wheeze: ExaminedResultEnum.NoProblem,
  cardiopulmonary99: ExaminedResultEnum.NoProblem,
  thorax: ExaminedResultEnum.NoProblem,
  c99: ExaminedResultEnum.NoProblem,
  abdomen: ExaminedResultEnum.NoProblem,
  a99: ExaminedResultEnum.NoProblem,
  scoliosis: ExaminedResultEnum.NoProblem,
  dysmelia: ExaminedResultEnum.NoProblem,
  polydactyly: ExaminedResultEnum.NoProblem,
  dysarthrosis: ExaminedResultEnum.NoProblem,
  dysmelia99: ExaminedResultEnum.NoProblem,
  squatting: ExaminedResultEnum.NoProblem,
  l99: ExaminedResultEnum.NoProblem,
  cryptorchidism: ExaminedResultEnum.NoExamined,
  prepuce: ExaminedResultEnum.NoProblem,
  varicocele: ExaminedResultEnum.NoProblem,
  u99: ExaminedResultEnum.NoProblem,
  epidermophytosis: ExaminedResultEnum.NoProblem,
  scabies: ExaminedResultEnum.NoProblem,
  wart: ExaminedResultEnum.NoProblem,
  atopicDermatitis: ExaminedResultEnum.NoProblem,
  eczema: ExaminedResultEnum.NoProblem,
  d99: ExaminedResultEnum.NoProblem,
  t01: ExaminedResultEnum.NoProblem,
  t02: ExaminedResultEnum.NoProblem,
  t03: ExaminedResultEnum.NoProblem,
  t04: ExaminedResultEnum.NoProblem,
  t05: ExaminedResultEnum.NoProblem,
  t06: ExaminedResultEnum.NoExamined,
  t07: ExaminedResultEnum.NoProblem,
  t08: ExaminedResultEnum.NoProblem,
  t99: ExaminedResultEnum.NoProblem,
  t09: ExaminedResultEnum.NoExamined,
  t11: TeeInspection.None,
  t12: TeeInspection.None,
  t13: TeeInspection.None,
  t15: TeeInspection.NoInspection,
  t16: ExaminedResultEnum.NoProblem,
  t17: ExaminedResultEnum.NoProblem,
  t18: ExaminedResultEnum.NoProblem,
  t19: ExaminedResultEnum.NoProblem,
  strabismus: ExaminedResultEnum.NoProblem,
  strabismusType: StrabismusKindEnum.None,
  trichiasis: ExaminedResultEnum.NoExamined,
  nystagmus: ExaminedResultEnum.NoExamined,
  blepharoptosis: ExaminedResultEnum.NoExamined,
  clp: ExaminedResultEnum.NoExamined,
  articulationDisorders: ExaminedResultEnum.NoExamined,
  preauricularFistula: ExaminedResultEnum.NoExamined,
  preauricularFistulaLR: LeftRightEnum.None,
  rhinitis: ExaminedResultEnum.NoExamined,
  allergicRhinitis: ExaminedResultEnum.NoExamined,
  hernia: ExaminedResultEnum.NoExamined,
  edema: ExaminedResultEnum.NoExamined,
  scrotalSwelling: ExaminedResultEnum.NoExamined,
  purpura: ExaminedResultEnum.NoExamined,
  someProblem: false,
  finished: false,
  trace: false,
  limit: false,
  assist: false,
  special: false,
  subjects: [],
  finishSubjects: [],
  traceItems: [],
};

const NoExaminedPhysical: PHIPhysical = {
  year: 0,
  sem: 0,
  grade: 0,
  yearClassId: 0,
  id: 0,
  bpResult: ExaminedResultEnum.NoExamined,
  waistResult: ExaminedResultEnum.NoExamined,
  pulseResult: ExaminedResultEnum.NoExamined,
  colorBlind: ExaminedResultEnum.NoExamined,
  e99: ExaminedResultEnum.NoExamined,
  hearing: ExaminedResultEnum.NoExamined,
  hearingLR: LeftRightEnum.None,
  earMisshapen: ExaminedResultEnum.NoExamined,
  earMisshapenLR: LeftRightEnum.None,
  eardrum: ExaminedResultEnum.NoExamined,
  eardrumLR: LeftRightEnum.None,
  cerumen: ExaminedResultEnum.NoExamined,
  cerumenLR: LeftRightEnum.None,
  tonsillitis: ExaminedResultEnum.NoExamined,
  o99: ExaminedResultEnum.NoExamined,
  torticollis: ExaminedResultEnum.NoExamined,
  mass: ExaminedResultEnum.NoExamined,
  goiter: ExaminedResultEnum.NoExamined,
  lymphadenectasis: ExaminedResultEnum.NoExamined,
  mass99: ExaminedResultEnum.NoExamined,
  n99: ExaminedResultEnum.NoExamined,
  cardiopulmonary: ExaminedResultEnum.NoExamined,
  heartMurmur: ExaminedResultEnum.NoExamined,
  arrhythmia: ExaminedResultEnum.NoExamined,
  wheeze: ExaminedResultEnum.NoExamined,
  cardiopulmonary99: ExaminedResultEnum.NoExamined,
  thorax: ExaminedResultEnum.NoExamined,
  c99: ExaminedResultEnum.NoExamined,
  abdomen: ExaminedResultEnum.NoExamined,
  a99: ExaminedResultEnum.NoExamined,
  scoliosis: ExaminedResultEnum.NoExamined,
  dysmelia: ExaminedResultEnum.NoExamined,
  polydactyly: ExaminedResultEnum.NoExamined,
  dysarthrosis: ExaminedResultEnum.NoExamined,
  dysmelia99: ExaminedResultEnum.NoExamined,
  squatting: ExaminedResultEnum.NoExamined,
  l99: ExaminedResultEnum.NoExamined,
  cryptorchidism: ExaminedResultEnum.NoExamined,
  prepuce: ExaminedResultEnum.NoExamined,
  varicocele: ExaminedResultEnum.NoExamined,
  u99: ExaminedResultEnum.NoExamined,
  epidermophytosis: ExaminedResultEnum.NoExamined,
  scabies: ExaminedResultEnum.NoExamined,
  wart: ExaminedResultEnum.NoExamined,
  atopicDermatitis: ExaminedResultEnum.NoExamined,
  eczema: ExaminedResultEnum.NoExamined,
  d99: ExaminedResultEnum.NoExamined,
  t01: ExaminedResultEnum.NoExamined,
  t02: ExaminedResultEnum.NoExamined,
  t03: ExaminedResultEnum.NoExamined,
  t04: ExaminedResultEnum.NoExamined,
  t05: ExaminedResultEnum.NoExamined,
  t06: ExaminedResultEnum.NoExamined,
  t07: ExaminedResultEnum.NoExamined,
  t08: ExaminedResultEnum.NoExamined,
  t99: ExaminedResultEnum.NoExamined,
  t09: ExaminedResultEnum.NoExamined,
  t11: TeeInspection.None,
  t12: TeeInspection.None,
  t13: TeeInspection.None,
  t15: TeeInspection.None,
  t16: ExaminedResultEnum.NoExamined,
  t17: ExaminedResultEnum.NoExamined,
  t18: ExaminedResultEnum.NoExamined,
  t19: ExaminedResultEnum.NoExamined,
  strabismus: ExaminedResultEnum.NoExamined,
  strabismusType: StrabismusKindEnum.None,
  trichiasis: ExaminedResultEnum.NoExamined,
  nystagmus: ExaminedResultEnum.NoExamined,
  blepharoptosis: ExaminedResultEnum.NoExamined,
  clp: ExaminedResultEnum.NoExamined,
  articulationDisorders: ExaminedResultEnum.NoExamined,
  preauricularFistula: ExaminedResultEnum.NoExamined,
  preauricularFistulaLR: LeftRightEnum.None,
  rhinitis: ExaminedResultEnum.NoExamined,
  allergicRhinitis: ExaminedResultEnum.NoExamined,
  hernia: ExaminedResultEnum.NoExamined,
  edema: ExaminedResultEnum.NoExamined,
  scrotalSwelling: ExaminedResultEnum.NoExamined,
  purpura: ExaminedResultEnum.NoExamined,
  someProblem: false,
  finished: false,
  trace: false,
  limit: false,
  assist: false,
  special: false,
  subjects: [],
  finishSubjects: [],
  traceItems: [],
};

const BPKey = ['bpResult', 'pulseResult'] as const;
const EyeKey = [
  'colorBlind',
  'strabismus',
  'trichiasis',
  'nystagmus',
  'blepharoptosis',
  'e99',
] as const;
const ENTKey = [
  'hearing',
  'eardrum',
  'earMisshapen',
  'clp',
  'articulationDisorders',
  'preauricularFistula',
  'cerumen',
  'rhinitis',
  'allergicRhinitis',
  'tonsillitis',
  'o99',
] as const;
const NeckKey = [
  'torticollis',
  'mass',
  'goiter',
  'lymphadenectasis',
  'mass99',
  'n99',
] as const;
const ChestKey = [
  'cardiopulmonary',
  'heartMurmur',
  'arrhythmia',
  'wheeze',
  'cardiopulmonary99',
  'thorax',
  'c99',
] as const;
const AbdomenKey = ['abdomen', 'hernia', 'a99'] as const;
const SpineKey = [
  'scoliosis',
  'dysmelia',
  'polydactyly',
  'dysarthrosis',
  'dysmelia99',
  'squatting',
  'edema',
  'l99',
] as const;
const UrogenitalKey = [
  'cryptorchidism',
  'scrotalSwelling',
  'prepuce',
  'varicocele',
  'u99',
] as const;
const SkinKey = [
  'epidermophytosis',
  'wart',
  'purpura',
  'scabies',
  'eczema',
  'atopicDermatitis',
  'd99',
] as const;
const OralRegularKey = ['t11', 't12', 't13', 't15'] as const;
const OralFollowKey = [
  't01',
  't04',
  't05',
  't08',
  't07',
  't09',
  't16',
  't17',
  't03',
  't18',
  't02',
  't19',
  't99',
] as const;
const OralStatusKey = [
  't01',
  't11',
  't12',
  't13',
  't15',
  't04',
  't05',
  't08',
  't07',
  't09',
  't16',
  't17',
  't03',
  't18',
  't02',
  't19',
  't99',
] as const;
const ManageKey = [
  'someProblem',
  'finished',
  'trace',
  'limit',
  'assist',
  'special',
  'memos',
] as const;

const ManageTraceKey = [
  'colorBlind',
  'strabismus',
  'trichiasis',
  'nystagmus',
  'blepharoptosis',

  'hearing',
  'eardrum',
  'earMisshapen',
  'clp',
  'articulationDisorders',
  'preauricularFistula',
  'cerumen',
  'rhinitis',
  'allergicRhinitis',
  'tonsillitis',

  'torticollis',
  'mass',
  'goiter',
  'lymphadenectasis',
  'mass99',

  'thorax',
  'heartMurmur',
  'arrhythmia',
  'wheeze',

  'abdomen',
  'hernia',

  'scoliosis',
  'polydactyly',
  'squatting',
  'dysarthrosis',
  'edema',

  'cryptorchidism',
  'scrotalSwelling',
  'prepuce',
  'varicocele',

  'epidermophytosis',
  'wart',
  'purpura',
  'scabies',
  'eczema',
  'atopicDermatitis',

  't01',
  't02',
  't04',
  't05',
  't08',
  't06',
  't07',
  't09',
] as const;

export type PageData = {
  id: number;
  year: number;
  sem: number;
  grade: number;
  sbp?: number | null;
  dbp?: number | null;
  bpResult: ExaminedResultEnum;
  waistline?: number | null;
  waistResult: ExaminedResultEnum;
  pulse?: number | null;
  pulseResult: ExaminedResultEnum;
  colorBlind: ExaminedResultEnum;
  e99: ExaminedResultEnum;
  e99State?: string | null;
  hearing: ExaminedResultEnum;
  hearingLR: LeftRightEnum;
  earMisshapen: ExaminedResultEnum;
  earMisshapenLR: LeftRightEnum;
  eardrum: ExaminedResultEnum;
  eardrumLR: LeftRightEnum;
  cerumen: ExaminedResultEnum;
  cerumenLR: LeftRightEnum;
  tonsillitis: ExaminedResultEnum;
  o99: ExaminedResultEnum;
  o99State?: string | null;
  torticollis: ExaminedResultEnum;
  mass: ExaminedResultEnum;
  goiter: ExaminedResultEnum;
  lymphadenectasis: ExaminedResultEnum;
  mass99: ExaminedResultEnum;
  n99: ExaminedResultEnum;
  n99State?: string | null;
  cardiopulmonary: ExaminedResultEnum;
  heartMurmur: ExaminedResultEnum;
  arrhythmia: ExaminedResultEnum;
  wheeze: ExaminedResultEnum;
  cardiopulmonary99: ExaminedResultEnum;
  thorax: ExaminedResultEnum;
  c99: ExaminedResultEnum;
  c99State?: string | null;
  abdomen: ExaminedResultEnum;
  a99: ExaminedResultEnum;
  a99State?: string | null;
  scoliosis: ExaminedResultEnum;
  dysmelia: ExaminedResultEnum;
  polydactyly: ExaminedResultEnum;
  dysarthrosis: ExaminedResultEnum;
  dysmelia99: ExaminedResultEnum;
  squatting: ExaminedResultEnum;
  l99: ExaminedResultEnum;
  l99State?: string | null;
  cryptorchidism: ExaminedResultEnum;
  prepuce: ExaminedResultEnum;
  varicocele: ExaminedResultEnum;
  u99: ExaminedResultEnum;
  u99State?: string | null;
  epidermophytosis: ExaminedResultEnum;
  scabies: ExaminedResultEnum;
  wart: ExaminedResultEnum;
  atopicDermatitis: ExaminedResultEnum;
  eczema: ExaminedResultEnum;
  d99: ExaminedResultEnum;
  d99State?: string | null;
  t01: TeeInitialExaminationEnum;
  ret01?: TeeFollowUpExaminationEnum | null;
  t02: TeeInitialExaminationEnum;
  ret02?: TeeFollowUpExaminationEnum | null;
  t03: TeeInitialExaminationEnum;
  ret03?: TeeFollowUpExaminationEnum | null;
  t04: TeeInitialExaminationEnum;
  ret04?: TeeFollowUpExaminationEnum | null;
  t06: number;
  t05: TeeInitialExaminationEnum;
  ret05?: TeeFollowUpExaminationEnum | null;
  t07: TeeInitialExaminationEnum;
  ret07?: TeeFollowUpExaminationEnum | null;
  t08: TeeInitialExaminationEnum;
  ret08?: TeeFollowUpExaminationEnum | null;
  t99: TeeInitialExaminationEnum;
  ret99?: TeeFollowUpExaminationEnum | null;
  t99state?: string | null;
  tSheet?: Record<string, string> | null;
  t09: TeeInitialExaminationEnum;
  ret09?: TeeFollowUpExaminationEnum | null;
  t11: TeeInspection;
  t12: TeeInspection;
  t13: TeeInspection;
  t15: TeeInspection;
  t16: TeeInitialExaminationEnum;
  ret16?: TeeFollowUpExaminationEnum | null;
  t17: TeeInitialExaminationEnum;
  ret17?: TeeFollowUpExaminationEnum | null;
  t18: TeeInitialExaminationEnum;
  ret18?: TeeFollowUpExaminationEnum | null;
  t19: TeeInitialExaminationEnum;
  ret19?: TeeFollowUpExaminationEnum | null;
  reT99state?: string | null;
  reTSheet?: Record<string, string> | null;
  strabismus: ExaminedResultEnum;
  strabismusType: StrabismusKindEnum;
  trichiasis: ExaminedResultEnum;
  nystagmus: ExaminedResultEnum;
  blepharoptosis: ExaminedResultEnum;
  clp: ExaminedResultEnum;
  articulationDisorders: ExaminedResultEnum;
  preauricularFistula: ExaminedResultEnum;
  preauricularFistulaLR: LeftRightEnum;
  rhinitis: ExaminedResultEnum;
  allergicRhinitis: ExaminedResultEnum;
  hernia: ExaminedResultEnum;
  edema: ExaminedResultEnum;
  scrotalSwelling: ExaminedResultEnum;
  purpura: ExaminedResultEnum;
  examDate?: DateTime | null;

  someProblem: boolean;
  subjects: string[];
  finished: boolean;
  finishSubjects: string[];
  trace: boolean;
  traceItems: string[];
  limit: boolean;
  assist: boolean;
  special: boolean;
  memos?: string | null;

  yearClassId: number;
  schoolId?: string | null;
  examination?: number | null;
  examinationState?: string | null;
  teeExamination?: number | null;
  teeExaminationState?: string | null;
};
